import api from './api';

/**
 * Servicio para gestionar torneos activos y jornadas
 * 
 * Este servicio maneja:
 * - Consulta de torneos activos
 * - Torneos por sede específica
 * - Próximas jornadas
 * - Estadísticas de torneos
 */
class TorneoService {
  
  /**
   * Obtiene todos los torneos (alias para getTorneosActivos)
   * @returns {Promise<Object>} Respuesta con torneos
   */
  getTorneos() {
    return this.getTorneosActivos();
  }

  /**
   * Obtiene todos los torneos activos
   * Un torneo es activo si tiene jornadas programadas en fecha futura
   * @returns {Promise<Object>} Respuesta con torneos activos
   */
  async getTorneosActivos() {
    try {
      const response = await api.get('/postgres/torneos/activos');
      return {
        success: true,
        data: response.data.data || [],
        count: response.data.count || 0
      };
    } catch (error) {
      console.error('Error al obtener torneos activos:', error);
      return {
        success: false,
        data: [],
        count: 0,
        error: error.response?.data?.message || 'Error al obtener torneos activos'
      };
    }
  }

  /**
   * Obtiene torneos activos en una sede específica
   * @param {number} sedeId - ID de la sede
   * @returns {Promise<Object>} Respuesta con torneos en la sede
   */
  async getTorneosActivosEnSede(sedeId) {
    try {
      const response = await api.get(`/postgres/torneos/activos/sede/${sedeId}`);
      return {
        success: true,
        data: response.data.data || [],
        count: response.data.count || 0,
        sede_id: sedeId
      };
    } catch (error) {
      console.error(`Error al obtener torneos en sede ${sedeId}:`, error);
      return {
        success: false,
        data: [],
        count: 0,
        sede_id: sedeId,
        error: error.response?.data?.message || 'Error al obtener torneos en sede'
      };
    }
  }

  /**
   * Obtiene la próxima jornada más cercana
   * @param {number} sedeId - ID de la sede (opcional)
   * @returns {Promise<Object>} Respuesta con la próxima jornada
   */
  async getProximaJornada(sedeId = null) {
    try {
      const url = sedeId 
        ? `/postgres/torneos/jornadas/proxima?sedeId=${sedeId}`
        : '/postgres/torneos/jornadas/proxima';
      
      const response = await api.get(url);
      return {
        success: true,
        data: response.data.data,
        sede_id: sedeId
      };
    } catch (error) {
      console.error('Error al obtener próxima jornada:', error);
      return {
        success: false,
        data: null,
        sede_id: sedeId,
        error: error.response?.data?.message || 'Error al obtener próxima jornada'
      };
    }
  }

  /**
   * Obtiene estadísticas generales de torneos
   * @returns {Promise<Object>} Respuesta con estadísticas
   */
  async getEstadisticasTorneos() {
    try {
      const response = await api.get('/postgres/torneos/stats');
      return {
        success: true,
        data: response.data.data || {}
      };
    } catch (error) {
      console.error('Error al obtener estadísticas de torneos:', error);
      return {
        success: false,
        data: {},
        error: error.response?.data?.message || 'Error al obtener estadísticas'
      };
    }
  }

  /**
   * Obtiene información completa de torneos para mostrar en badges
   * @param {number} sedeId - ID de la sede (opcional)
   * @returns {Promise<Object>} Información formateada para badges
   */
  async getInfoTorneosParaBadges(sedeId = null) {
    try {
      const [torneosActivos, proximaJornada, estadisticas] = await Promise.all([
        sedeId ? this.getTorneosActivosEnSede(sedeId) : this.getTorneosActivos(),
        this.getProximaJornada(sedeId),
        this.getEstadisticasTorneos()
      ]);

      const tieneTorneosActivos = torneosActivos.success && torneosActivos.count > 0;
      const tieneProximaJornada = proximaJornada.success && proximaJornada.data;

      return {
        success: true,
        tiene_torneos_activos: tieneTorneosActivos,
        torneos_activos: {
          count: torneosActivos.count,
          data: torneosActivos.data
        },
        proxima_jornada: tieneProximaJornada ? {
          fecha: proximaJornada.data.fecha,
          hora: proximaJornada.data.hora,
          torneo_nombre: proximaJornada.data.torneo_nombre,
          sede_nombre: proximaJornada.data.sede_nombre,
          sede_poblacion: proximaJornada.data.sede_poblacion,
          numero: proximaJornada.data.numero,
          fecha_limite_inscripcion: proximaJornada.data.fecha_limite_inscripcion
        } : null,
        estadisticas: estadisticas.data,
        sede_id: sedeId
      };
    } catch (error) {
      console.error('Error al obtener info de torneos para badges:', error);
      return {
        success: false,
        tiene_torneos_activos: false,
        torneos_activos: { count: 0, data: [] },
        proxima_jornada: null,
        estadisticas: {},
        sede_id: sedeId,
        error: 'Error al obtener información de torneos'
      };
    }
  }

  /**
   * Formatea una fecha para mostrar en la interfaz
   * @param {string} fecha - Fecha en formato ISO
   * @returns {string} Fecha formateada
   */
  formatearFecha(fecha) {
    if (!fecha) return '';
    
    try {
      const date = new Date(fecha);
      return date.toLocaleDateString('es-ES', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return fecha;
    }
  }

  /**
   * Formatea una hora para mostrar en la interfaz
   * @param {string} hora - Hora en formato HH:MM:SS
   * @returns {string} Hora formateada
   */
  formatearHora(hora) {
    if (!hora) return '';
    
    try {
      // Convertir HH:MM:SS a HH:MM
      return hora.substring(0, 5);
    } catch (error) {
      return hora;
    }
  }

  /**
   * Calcula los días restantes hasta una fecha
   * @param {string} fecha - Fecha objetivo
   * @returns {number} Días restantes (negativo si ya pasó)
   */
  calcularDiasRestantes(fecha) {
    if (!fecha) return null;

    try {
      const fechaObjetivo = new Date(fecha);
      const fechaActual = new Date();
      const diferencia = fechaObjetivo.getTime() - fechaActual.getTime();
      return Math.ceil(diferencia / (1000 * 3600 * 24));
    } catch (error) {
      return null;
    }
  }

  // ===== FUNCIONES CRUD PARA ADMINISTRADORES =====

  /**
   * Obtener torneos por organización
   * @param {number} organizacionId - ID de la organización
   * @returns {Promise<Object>} Lista de torneos de la organización
   */
  async getTorneosPorOrganizacion(organizacionId) {
    try {
      const response = await api.get(`/postgres/torneos/organizacion/${organizacionId}`);
      return {
        success: true,
        data: response.data.data || [],
        count: response.data.count || 0
      };
    } catch (error) {
      console.error('Error al obtener torneos por organización:', error);
      return {
        success: false,
        data: [],
        count: 0,
        error: error.response?.data?.message || 'Error al obtener torneos por organización'
      };
    }
  }

  /**
   * Obtener futbolistas inscritos en un torneo
   * @param {number} torneoId - ID del torneo
   * @returns {Promise<Object>} Lista de futbolistas inscritos
   */
  async getFutbolistasEnTorneo(torneoId) {
    try {
      const response = await api.get(`/postgres/torneos/${torneoId}/futbolistas`);
      return {
        success: true,
        data: response.data.data || [],
        count: response.data.count || 0,
        torneo: response.data.torneo || null
      };
    } catch (error) {
      console.error('Error al obtener futbolistas del torneo:', error);
      return {
        success: false,
        data: [],
        count: 0,
        torneo: null,
        error: error.response?.data?.message || 'Error al obtener futbolistas del torneo'
      };
    }
  }

  /**
   * Obtener jornadas de un torneo con futbolistas inscritos
   * @param {number} torneoId - ID del torneo
   * @returns {Promise<Object>} Lista de jornadas con futbolistas inscritos
   */
  async getJornadasConFutbolistas(torneoId) {
    try {
      const response = await api.get(`/postgres/torneos/${torneoId}/jornadas`);
      return {
        success: true,
        data: response.data.data || [],
        count: response.data.count || 0,
        torneo: response.data.torneo || null
      };
    } catch (error) {
      console.error('Error al obtener jornadas del torneo:', error);
      return {
        success: false,
        data: [],
        count: 0,
        torneo: null,
        error: error.response?.data?.message || 'Error al obtener jornadas del torneo'
      };
    }
  }

  /**
   * Obtener torneo por ID
   * @param {number} id - ID del torneo
   * @returns {Promise<Object>} Torneo
   */
  async getTorneo(id) {
    try {
      const response = await api.get(`/postgres/torneos/${id}`);
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      console.error('Error al obtener torneo:', error);
      return {
        success: false,
        data: null,
        error: error.response?.data?.message || 'Error al obtener torneo'
      };
    }
  }

  /**
   * Crear nuevo torneo
   * @param {Object} torneoData - Datos del torneo
   * @returns {Promise<Object>} Torneo creado
   */
  async createTorneo(torneoData) {
    try {
      const response = await api.post('/postgres/torneos', torneoData);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || 'Torneo creado exitosamente'
      };
    } catch (error) {
      console.error('Error al crear torneo:', error);
      return {
        success: false,
        data: null,
        error: error.response?.data?.message || 'Error al crear torneo'
      };
    }
  }

  /**
   * Actualizar torneo
   * @param {number} id - ID del torneo
   * @param {Object} torneoData - Datos actualizados
   * @returns {Promise<Object>} Torneo actualizado
   */
  async updateTorneo(id, torneoData) {
    try {
      const response = await api.put(`/postgres/torneos/${id}`, torneoData);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || 'Torneo actualizado exitosamente'
      };
    } catch (error) {
      console.error('Error al actualizar torneo:', error);
      return {
        success: false,
        data: null,
        error: error.response?.data?.message || 'Error al actualizar torneo'
      };
    }
  }

  /**
   * Eliminar torneo
   * @param {number} id - ID del torneo
   * @returns {Promise<Object>} Confirmación
   */
  async deleteTorneo(id) {
    try {
      const response = await api.delete(`/postgres/torneos/${id}`);
      return {
        success: true,
        message: response.data.message || 'Torneo eliminado exitosamente'
      };
    } catch (error) {
      console.error('Error al eliminar torneo:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Error al eliminar torneo'
      };
    }
  }

  // ===== FUNCIONES PARA GESTIÓN DE JORNADAS =====

  /**
   * Obtener jornadas de un torneo
   * @param {number} torneoId - ID del torneo
   * @returns {Promise<Object>} Lista de jornadas del torneo
   */
  async getJornadasTorneo(torneoId) {
    try {
      const response = await api.get(`/postgres/jornadas?torneo_id=${torneoId}`);
      return {
        success: true,
        data: response.data.data || [],
        count: response.data.count || 0
      };
    } catch (error) {
      console.error('Error al obtener jornadas del torneo:', error);
      return {
        success: false,
        data: [],
        count: 0,
        error: error.response?.data?.message || 'Error al obtener jornadas del torneo'
      };
    }
  }

  /**
   * Crear jornada en una sede del torneo
   */
  async createJornada(torneoId, sedeEnTorneoId, data) {
    try {
      const response = await api.post(`/postgres/torneos/${torneoId}/sedes/${sedeEnTorneoId}/jornadas`, data);
      return { success: true, data: response.data.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Error al crear jornada' };
    }
  }

  /**
   * Actualizar jornada
   */
  async updateJornada(torneoId, jornadaId, data) {
    try {
      const response = await api.put(`/postgres/torneos/${torneoId}/jornadas/${jornadaId}`, data);
      return { success: true, data: response.data.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Error al actualizar jornada' };
    }
  }

  /**
   * Eliminar jornada
   */
  async deleteJornada(torneoId, jornadaId) {
    try {
      await api.delete(`/postgres/torneos/${torneoId}/jornadas/${jornadaId}`);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Error al eliminar jornada' };
    }
  }

  /**
   * Añadir sede a un torneo
   */
  async addSedeToTorneo(torneoId, sedeId, nombreInterno, equipacionNueva = false) {
    try {
      const response = await api.post(`/postgres/torneos/${torneoId}/sedes`, {
        sede_id: sedeId,
        nombre_interno: nombreInterno,
        equipacion_nueva: equipacionNueva
      });
      return { success: true, data: response.data.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Error al añadir sede' };
    }
  }

  /**
   * Quitar sede de un torneo
   */
  async removeSedeFromTorneo(torneoId, sedeEnTorneoId) {
    try {
      await api.delete(`/postgres/torneos/${torneoId}/sedes/${sedeEnTorneoId}`);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Error al quitar sede' };
    }
  }

  /**
   * Actualizar parámetros de una sede en el torneo
   */
  async updateSedeEnTorneo(torneoId, sedeEnTorneoId, data) {
    try {
      const response = await api.put(`/postgres/torneos/${torneoId}/sedes/${sedeEnTorneoId}`, data);
      return { success: true, data: response.data.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Error al actualizar sede en torneo' };
    }
  }

  /**
   * Obtener jornadas de una sede en un torneo
   */
  async getJornadasDeSede(torneoId, sedeEnTorneoId) {
    try {
      const response = await api.get(`/postgres/torneos/${torneoId}/sedes/${sedeEnTorneoId}/jornadas`);
      return { success: true, data: response.data.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Error al obtener jornadas' };
    }
  }

}

// Exportar instancia única del servicio
const torneoService = new TorneoService();
export default torneoService;
